import { useApolloClient, useLazyQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { CharacterizationBasis, PackingGroup, PhysicalState } from "generated-graphql/graphql";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import {
  GET_WASTE_STREAM_PROFILE,
  useSetWasteStreamDotProfile,
  useUpsertWasteProfile,
  WASTE_PROFILE_INPUT_VALIDATOR,
} from "./api";
import {
  prettyPrintCharacterizationBasis,
  prettyPrintPackingGroup,
  prettyPrintPhysicalState,
} from "./profileDisplay";
import { WasteStreamProfileData } from "./types";
import {
  isBlankConstituentRow,
  NUMERIC_FIELD_RULES,
  toDotInput,
  toFormValues,
  toProfileInput,
  WasteProfileFormValues,
} from "./wasteProfileForm";

const HALF = { xs: 12, sm: 6 };
const THIRD = { xs: 12, sm: 4 };

// The formatters return undefined for an unset value so read views can render a
// blank; here every enum member is mapped, so the fallback is unreachable and
// exists only to keep the option label a string.
const physicalStateItems = Object.values(PhysicalState).map((value) => ({
  value,
  display: prettyPrintPhysicalState(value) ?? value,
}));

const basisItems = Object.values(CharacterizationBasis).map((value) => ({
  value,
  display: prettyPrintCharacterizationBasis(value) ?? value,
}));

const packingGroupItems = Object.values(PackingGroup).map((value) => ({
  value,
  display: prettyPrintPackingGroup(value) ?? value,
}));

export const EditWasteProfileDialog = ({
  open,
  onClose,
  wasteStream,
}: {
  open: boolean;
  onClose: () => void;
  wasteStream: WasteStreamProfileData;
}) => {
  const alerts = useAlerts();
  const client = useApolloClient();
  // The parent mounts this dialog only while it is open, so defaultValues are
  // read fresh on every open and no reset effect is needed to pick up a save.
  const { control, handleSubmit, getValues } = useForm<WasteProfileFormValues>({
    defaultValues: toFormValues(wasteStream),
  });
  const { fields, append, remove } = useFieldArray({ control, name: "constituents" });
  const [issues, setIssues] = useState<string[]>([]);

  const [upsertProfile, { loading: savingProfile }] = useUpsertWasteProfile();
  const [setDotProfile, { loading: savingDot }] = useSetWasteStreamDotProfile();
  const [runValidator, { loading: validating }] = useLazyQuery(WASTE_PROFILE_INPUT_VALIDATOR, {
    fetchPolicy: "network-only",
  });

  // The final refetch is not tracked by any mutation hook, and a partial save
  // leaves the dialog open, so without this Save re-enables while the profile
  // is still reloading and a second click can fire against stale form state.
  const [refreshing, setRefreshing] = useState(false);
  const saving = savingProfile || savingDot || validating || refreshing;

  const onSubmit = async (values: WasteProfileFormValues) => {
    const input = toProfileInput(wasteStream.id, values);

    // The validator is the same rule set the mutation enforces server-side
    // (hazard/property consistency, pH range). Running it first turns what
    // would be a thrown BAD_USER_INPUT into inline, per-rule messages.
    //
    // A failure here has to be reported, not swallowed: the lazy query
    // rejects on a network or authorization error, and an unhandled rejection
    // would abandon the submit with the dialog still open and nothing said —
    // the same "Save did nothing" symptom this PR already fixed once. Its
    // resolved-but-errored form is just as bad, because an undefined result
    // reads as zero issues and would wave an unvalidated profile through.
    let validation;
    try {
      const result = await runValidator({ variables: { input } });
      if (result.error) throw result.error;
      validation = result.data;
    } catch (error) {
      alerts.error(
        "Could not validate the profile",
        error instanceof Error ? error.message : undefined
      );
      return;
    }
    const messages = (validation?.wasteProfileInputValidator ?? []).map((issue) => issue.message);
    if (messages.length > 0) {
      setIssues(messages);
      return;
    }
    setIssues([]);

    let profileCommitted = false;
    try {
      // Two mutations because the DOT block lives on WasteStream while the
      // characterization lives on WasteProfile. The profile goes first: it is
      // the row that may not exist yet, and a failure there should not leave
      // a DOT change stranded against a stream with no profile.
      //
      // Both suppress their hook's own refetch (`refetchQueries: []`) because
      // they refetch the SAME query. Left to their defaults, the profile
      // write's refetch is issued before the DOT write lands, so whichever
      // response arrives last wins — and when it's that first one, the DOT
      // block reads back as empty even though it saved. One refetch after
      // both writes is the only ordering that can't race.
      await upsertProfile({ variables: { input }, refetchQueries: [] });
      profileCommitted = true;
      await setDotProfile({
        variables: { input: toDotInput(wasteStream.id, values) },
        refetchQueries: [],
      });
      alerts.success("Profile saved");
      onClose();
    } catch (error) {
      const detail = error instanceof Error ? error.message : undefined;
      // The two writes aren't one transaction, so a DOT failure after the
      // profile committed is a partial save. Saying "failed to save profile"
      // there would send the user back to re-enter characterization fields
      // that are already stored.
      if (profileCommitted) {
        alerts.error("Characterization saved, but the DOT fields did not", detail);
      } else {
        alerts.error("Failed to save profile", detail);
      }
    } finally {
      // In `finally` so a failed DOT write still refreshes the profile write
      // that already committed, rather than leaving the drawer stale.
      setRefreshing(true);
      try {
        await client.refetchQueries({ include: [GET_WASTE_STREAM_PROFILE] });
      } finally {
        setRefreshing(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      title="Edit characterization"
      primaryActionButtonProps={{
        loading: saving,
        onClick: handleSubmit(onSubmit),
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        {issues.length > 0 && (
          <Alert severity="error">
            <AlertTitle>Fix these before saving</AlertTitle>
            <ul style={{ margin: 0, paddingInlineStart: "1.25rem" }}>
              {issues.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Stack spacing={2}>
          <Typography variant="subtitle2">Physical properties</Typography>
          <Grid container spacing={2}>
            <Grid size={THIRD}>
              <FormSelect
                name="physicalState"
                label="Physical state"
                control={control}
                hideErrors
                selectItems={physicalStateItems}
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="phLow"
                label="pH low"
                control={control}
                rules={NUMERIC_FIELD_RULES}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="phHigh"
                label="pH high"
                control={control}
                rules={NUMERIC_FIELD_RULES}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="flashPointF"
                label="Flash point (°F)"
                control={control}
                rules={NUMERIC_FIELD_RULES}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="specificGravity"
                label="Specific gravity"
                control={control}
                rules={NUMERIC_FIELD_RULES}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormSelect
                name="basis"
                label="Basis"
                control={control}
                hideErrors
                selectItems={basisItems}
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                name="physicalDescription"
                label="Physical description"
                control={control}
                fullWidth
                textFieldProps={{ multiline: true, minRows: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                name="generatingProcess"
                label="Generating process"
                control={control}
                fullWidth
                textFieldProps={{ multiline: true, minRows: 2 }}
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="subtitle2">Hazard characteristics</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {(
              [
                ["isIgnitable", "Ignitable"],
                ["isCorrosive", "Corrosive"],
                ["isReactive", "Reactive"],
                ["isToxic", "Toxic"],
              ] as const
            ).map(([name, label]) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    }
                    label={label}
                  />
                )}
              />
            ))}
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="subtitle2">Profile identity</Typography>
          <Grid container spacing={2}>
            <Grid size={THIRD}>
              <FormTextField
                name="profileNumber"
                label="Profile number"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField name="tsdfName" label="TSDF" control={control} fullWidth />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="tsdfProfileId"
                label="TSDF profile ID"
                control={control}
                fullWidth
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="subtitle2">DOT</Typography>
          <Grid container spacing={2}>
            <Grid size={HALF}>
              <FormTextField
                name="dotProperShippingName"
                label="Proper shipping name"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField name="dotIdNumber" label="UN/NA ID" control={control} fullWidth />
            </Grid>
            <Grid size={THIRD}>
              <FormTextField
                name="dotHazardClass"
                label="Hazard class"
                control={control}
                fullWidth
              />
            </Grid>
            <Grid size={THIRD}>
              <FormSelect
                name="dotPackingGroup"
                label="Packing group"
                control={control}
                hideErrors
                selectItems={packingGroupItems}
              />
            </Grid>
            <Grid size={THIRD}>
              <Controller
                name="dotHazardous"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    }
                    label="DOT hazardous"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Constituents</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => append({ name: "", casNumber: "", minPercent: "", maxPercent: "" })}
            >
              Add constituent
            </Button>
          </Stack>
          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No constituents yet.
            </Typography>
          )}
          {fields.map((field, index) => (
            <Grid container spacing={1} key={field.id} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  name={`constituents.${index}.name`}
                  label="Name"
                  control={control}
                  rules={{
                    // A row is dropped on save only when it is wholly empty,
                    // so once any other cell is filled the name has to be
                    // there — otherwise the save would succeed and quietly
                    // take the rest of the row with it.
                    validate: () => {
                      const row = getValues(`constituents.${index}`);
                      if (isBlankConstituentRow(row)) return true;
                      return row.name?.trim() ? true : "Name is required";
                    },
                  }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormTextField
                  name={`constituents.${index}.casNumber`}
                  label="CAS number"
                  control={control}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormTextField
                  name={`constituents.${index}.minPercent`}
                  label="Min %"
                  control={control}
                  rules={NUMERIC_FIELD_RULES}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormTextField
                  name={`constituents.${index}.maxPercent`}
                  label="Max %"
                  control={control}
                  rules={NUMERIC_FIELD_RULES}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 1 }}>
                <IconButton
                  aria-label={`Remove constituent ${getValues(`constituents.${index}.name`) || index + 1}`}
                  onClick={() => remove(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
};
